import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faCircle } from "@fortawesome/free-solid-svg-icons";

type UboStatus = {
  name: string;
  completed: boolean;
  idvLink?: string;
};

interface KybReviewStatusProps {
  ubos: UboStatus[];
  onContactSupport: () => void;
  caseCompleted?: boolean;
}

export default function KybReviewStatus({
  ubos,
  onContactSupport,
  caseCompleted,
}: KybReviewStatusProps) {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const allUbosCompleted =
    ubos.length > 0 && ubos.every((ubo) => ubo.completed);

  const uboStepActive = ubos.length > 0;

  const handleCopy = (link: string, idx: number) => {
    navigator.clipboard.writeText(link);
    setCopiedIndex(idx);
    setTimeout(() => setCopiedIndex(null), 1500);
  };

  return (
    <div className="bg-white rounded-lg p-8 max-w-3xl mx-auto">
      <div className="flex flex-col items-start mb-8">
        <h2 className="text-[20px] font-light text-gray-900 mt-6 mb-2">
          Here's what happens next
        </h2>
      </div>

      <div className="space-y-6 font-light">
        <div className="flex items-center">
          <FontAwesomeIcon
            icon={faCheckCircle}
            className="text-[#6333A4] w-5 h-5 mr-3"
          />
          <span className="text-gray-900 font-light">
            <span className="text-[#6333A4] font-medium">KYB</span> submitted
          </span>
        </div>

        <div>
          <div className="flex items-center mb-2">
            <FontAwesomeIcon
              icon={uboStepActive ? faCheckCircle : faCircle}
              className={`${uboStepActive ? "text-[#6333A4]" : "text-gray-300"} w-5 h-5 mr-3`}
            />
            <span className={`text-gray-900`}>
              Every <span className="text-[#6333A4] font-medium">UBO</span> in
              the KYB gets an email to complete{" "}
              <span className="text-[#6333A4] font-medium">IDV</span> (Check
              your spam folder)
            </span>
          </div>

          <div className="ml-8 space-y-2">
            {ubos.map((ubo, index) => (
              <div key={index} className="flex items-center">
                <FontAwesomeIcon
                  icon={ubo.completed ? faCheckCircle : faCircle}
                  className={`w-4 h-4 mr-3 ${
                    ubo.completed ? "text-[#6333A4]" : "text-gray-300"
                  }`}
                />
                <span
                  className={`${
                    ubo.completed ? "text-gray-900" : "text-gray-500"
                  }`}
                >
                  {ubo.name}
                </span>
                <span className="ml-auto text-sm text-gray-500 flex items-center gap-2">
                  {ubo.completed ? (
                    "Completed"
                  ) : (
                    <>
                      Pending
                      {ubo.idvLink && (
                        <button
                          className="ml-2 text-[#6333A4] underline text-xs"
                          onClick={() => handleCopy(ubo.idvLink!, index)}
                        >
                          {copiedIndex === index ? "Copied!" : "Copy Link"}
                        </button>
                      )}
                    </>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center">
          <FontAwesomeIcon
            icon={allUbosCompleted ? faCheckCircle : faCircle}
            className={`${allUbosCompleted ? "text-[#6333A4]" : "text-gray-300"} w-5 h-5 mr-3`}
          />
          <span
            className={`${allUbosCompleted ? "text-gray-900" : "text-gray-500"}`}
          >
            All IDVs completed
          </span>
        </div>

        <div className="flex items-center">
          <FontAwesomeIcon
            icon={caseCompleted ? faCheckCircle : faCircle}
            className={`${caseCompleted ? "text-[#6333A4]" : "text-gray-300"} w-5 h-5 mr-3`}
          />
          <span
            className={`${caseCompleted ? "text-gray-900" : "text-gray-500"}`}
          >
            Compliance approval / rejection
          </span>
        </div>
      </div>

      <button
        onClick={onContactSupport}
        className="mt-8 w-full text-[#6333A4] font-medium py-2 px-4 rounded-lg border border-[#6333A4] hover:bg-[#6333A4]/5 transition-colors"
      >
        Questions? Contact us
      </button>
    </div>
  );
}
